import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { AlignBottomIcon } from '@/components/tiptap-editor/tiptap-icons/align-bottom-icon';
import { AlignTopIcon } from '@/components/tiptap-editor/tiptap-icons/align-top-icon';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button as UiButton } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { moveNode } from '@/hooks/use-move-node';
import { useSiblingNodeInfo } from '@/hooks/use-sibling-node-info';
import type { ReactNodeViewProps } from '@tiptap/react';
import { NodeViewContent } from '@tiptap/react';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { memo, useState } from 'react';
import { countChildType, getChildText, sumGoalPoints } from './zyitlab-shared';

export const ZyITLabStepNodeView: React.FC<ReactNodeViewProps> = memo(({ node, selected, editor, getPos, deleteNode, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const [open, setOpen] = useState(false);
    const { nodeIndex, totalNodes } = useSiblingNodeInfo({
        editor,
        getPos,
        nodeTypeName: 'zyITLabStep',
    });
    const pos = getPos();
    const goalCount = countChildType(node, 'zyITLabGoal');
    const stepPoints = sumGoalPoints(node);

    const stepNumber = nodeIndex + 1;

    const handleMoveUp = () => {
        if (typeof pos !== 'number') return;
        moveNode(editor, 'up', { pos, node });
    };

    const handleMoveDown = () => {
        if (typeof pos !== 'number') return;
        moveNode(editor, 'down', { pos, node });
    };

    const handleDelete = () => {
        if (totalNodes <= 1) return;
        if (window.confirm('Delete this step?')) {
            deleteNode();
        }
    };

    const summaryLabel = getChildText(node, 'zyITLabStepInstructions').trim().slice(0, 80) || `Step ${stepNumber}`;

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label={`Step ${stepNumber}`}
            selected={selected}
            containerClassName="mb-2 rounded-md border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50"
            contentClassName="px-3"
        >
            <Collapsible
                open={open}
                onOpenChange={setOpen}
            >
                <div
                    className="flex items-center gap-2 px-3 py-4"
                    contentEditable={false}
                >
                    <CollapsibleTrigger className="flex flex-1 items-center gap-2 text-left">
                        <ChevronDown className={`h-4 w-4 transition-transform ${open ? '' : '-rotate-90'}`} />
                        <div className="flex items-center gap-6">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{summaryLabel}</div>
                            <div className="text-sm text-gray-500">
                                {goalCount} goal{goalCount === 1 ? '' : 's'} · {stepPoints} point{stepPoints === 1 ? '' : 's'}
                            </div>
                        </div>
                    </CollapsibleTrigger>
                    <div className="flex items-center gap-1">
                        <Button
                            onClick={handleMoveUp}
                            data-style="ghost"
                            disabled={nodeIndex === 0 || totalNodes <= 1 || isEditorReadonly}
                            aria-label="Move step up"
                        >
                            <AlignTopIcon className="h-4 w-4" />
                        </Button>
                        <Button
                            onClick={handleMoveDown}
                            data-style="ghost"
                            disabled={nodeIndex === totalNodes - 1 || totalNodes <= 1 || isEditorReadonly}
                            aria-label="Move step down"
                        >
                            <AlignBottomIcon className="h-4 w-4" />
                        </Button>
                        <Button
                            onClick={handleDelete}
                            data-style="ghost"
                            disabled={totalNodes <= 1 || isEditorReadonly}
                            aria-label="Delete step"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
                <CollapsibleContent>
                    <NodeViewContent />
                    <div
                        className="mb-2 flex justify-end gap-2"
                        contentEditable={false}
                    >
                        <UiButton
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isEditorReadonly}
                            onClick={() => editor.commands.addZyITLabGoal(getPos())}
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Add goal
                        </UiButton>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </CommonNodeViewWrapper>
    );
});

ZyITLabStepNodeView.displayName = 'ZyITLabStepNodeView';
